import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, onSnapshot, doc, updateDoc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Heading } from './heading';
import { Dialog, DialogBody, DialogActions, DialogTextarea } from './dialog';
import { Button } from './button';
import {
  GlobeAltIcon,
  LinkIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid';
import { showAlert } from '../alertService';

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  try {
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'America/Mexico_City'
    }).format(date);
  } catch (error) {
    return 'Invalid date';
  }
};

const TableHeader = ({ children }) => (
  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    {children}
  </th>
);

const TableCell = ({ children, className = "" }) => (
  <td className={`px-6 py-4 whitespace-nowrap text-sm ${className}`}>
    {children}
  </td>
);

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente' },
    approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Aprobado' },
    rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rechazado' },
    denied: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rechazado' }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const ActionButtons = ({ onApprove, onReject }) => (
  <div className="flex space-x-2">
    <button
      onClick={onApprove}
      className="text-green-600 hover:text-green-900"
    >
      Aprobar
    </button>
    <button
      onClick={onReject}
      className="text-red-600 hover:text-red-900"
    >
      Rechazar
    </button>
  </div>
);

const VenueRequests = () => {
  const [venueRequests, setVenueRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conciergeRequests, setConciergeRequests] = useState([]);
  const [conciergeLoading, setConciergeLoading] = useState(true);
  const [planners, setPlanners] = useState([]);
  const [plannersLoading, setPlannersLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlanner, setSelectedPlanner] = useState(null);
  const [denialReason, setDenialReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeniedPlanners, setShowDeniedPlanners] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const venueRequestsQuery = query(collection(db, 'venueRequest'), orderBy('createdAt', 'desc'));
        const venueRequestsSnapshot = await getDocs(venueRequestsQuery);
        const venueRequestsData = venueRequestsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVenueRequests(venueRequestsData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching venue requests:', err);
        setError('Error fetching venue requests');
        setLoading(false);
      }
    };

    fetchData();

    // Fetch planners
    const fetchPlanners = async () => {
      try {
        const plannersQuery = query(collection(db, 'planners'), orderBy('createdAt', 'desc'));
        const plannersSnapshot = await getDocs(plannersQuery);
        const plannersData = plannersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPlanners(plannersData);
      } catch (err) {
        console.error('Error fetching planners:', err);
      } finally {
        setPlannersLoading(false);
      }
    };

    fetchPlanners();

    // Add concierge requests subscription
    const conciergeQuery = query(
      collection(db, 'concierge'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeConcierge = onSnapshot(conciergeQuery, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setConciergeRequests(requests);
      setConciergeLoading(false);
    });

    return () => {
      unsubscribeConcierge();
    };
  }, []);

  const handleUpdateStatus = async (id, status, type = 'venue') => {
    try {
      const docRef = doc(db, type === 'venue' ? 'venueRequest' : 'concierge', id);
      await updateDoc(docRef, {
        status,
        updatedAt: serverTimestamp()
      });
      showAlert(`Solicitud ${status === 'approved' ? 'aprobada' : 'rechazada'} exitosamente`, 'success');
    } catch (error) {
      console.error('Error updating status:', error);
      showAlert('Error al actualizar el estado', 'error');
    }
  };

  const handleApprovePlanner = async (planner) => {
    if (!planner || isProcessing) return;

    setIsProcessing(true);
    try {
      const plannerRef = doc(db, 'planners', planner.id);
      await updateDoc(plannerRef, {
        verified: true,
        denied: false,
        verifiedAt: new Date(),
      });

      const userRef = doc(db, 'users', planner.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        await updateDoc(userRef, {
          role: 'planner',
          updatedAt: new Date(),
        });
      } else {
        await setDoc(userRef, {
          uid: planner.uid,
          email: planner.email,
          name: planner.firstName,
          lastName: planner.lastName,
          role: 'planner',
          phone: planner.phone,
          createdAt: new Date(),
          lastLogin: new Date(),
        });
      }

      setPlanners(
        planners.map((p) =>
          p.id === planner.id ? { ...p, verified: true, denied: false } : p
        )
      );

      showAlert('Planner aprobado exitosamente', 'success');
    } catch (error) {
      console.error('Error approving planner:', error);
      showAlert('Error al aprobar la solicitud', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDenyPlanner = async () => {
    if (!selectedPlanner || !denialReason || isProcessing) return;

    setIsProcessing(true);
    try {
      const plannerRef = doc(db, 'planners', selectedPlanner.id);
      await updateDoc(plannerRef, {
        denied: true,
        verified: false,
        denialReason: denialReason,
        deniedAt: new Date(),
      });

      setPlanners(
        planners.map((p) =>
          p.id === selectedPlanner.id
            ? { ...p, denied: true, verified: false, denialReason }
            : p
        )
      );

      setIsModalOpen(false);
      setDenialReason('');
      setSelectedPlanner(null);
      showAlert('Planner rechazado', 'success');
    } catch (error) {
      console.error('Error denying planner:', error);
      showAlert('Error al rechazar la solicitud', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) return <div className="p-4">Cargando solicitudes...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="container mx-auto p-4 space-y-8 max-lg:px-0">
      <div className="sm:flex sm:items-center sm:justify-between">
        <Heading level={2}>Solicitudes de Venues</Heading>
        <div className="mt-4 sm:mt-0 sm:flex-none">
          <span className="text-sm text-gray-700">
            Total: {venueRequests.length} solicitudes
          </span>
        </div>
      </div>

      <div className="mt-4">
        <div className="overflow-x-auto ring-1 ring-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader>Venue</TableHeader>
                <TableHeader>Solicitante</TableHeader>
                <TableHeader>Contacto</TableHeader>
                <TableHeader>Ubicación</TableHeader>
                <TableHeader>Social</TableHeader>
                <TableHeader>Estado</TableHeader>
                <TableHeader>Fecha de Solicitud</TableHeader>
                <TableHeader>Acciones</TableHeader>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {venueRequests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50">
                  <TableCell>
                    <div className="text-sm font-medium text-gray-900">{request.venueName}</div>
                    {request.website && (
                      <a 
                        href={request.website.startsWith('http') ? request.website : `https://${request.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <GlobeAltIcon className="h-4 w-4" />
                        Website
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-500">
                            {request.requestedBy?.name ? request.requestedBy.name[0] : '?'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {request.requestedBy?.name} {request.requestedBy?.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {request.uid}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-900">{request.requestedBy?.email}</div>
                    <div className="text-sm text-gray-500">{request.requestedBy?.phone}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-900">{request.location}</div>
                  </TableCell>
                  <TableCell>
                    {request.instagram && (
                      <a 
                        href={`https://instagram.com/${request.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-pink-600 hover:text-pink-800 flex items-center gap-1"
                      >
                        <LinkIcon className="h-4 w-4" />
                        {request.instagram}
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={request.status} />
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-900">
                      {formatDate(request.createdAt)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <ActionButtons 
                      onApprove={() => handleUpdateStatus(request.id, 'approved', 'venue')}
                      onReject={() => handleUpdateStatus(request.id, 'denied', 'venue')}
                    />
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Concierge Requests Table */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <Heading level={3}>Solicitudes de Concierge</Heading>
          <div className="mt-4 sm:mt-0 sm:flex-none">
            <span className="text-sm text-gray-700">
              Total: {conciergeRequests.length} solicitudes
            </span>
          </div>
        </div>
        {conciergeLoading ? (
          <div>Cargando solicitudes...</div>
        ) : (
          <div className="mt-4 overflow-x-auto ring-1 ring-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <TableHeader>Fecha de Solicitud</TableHeader>
                  <TableHeader>Nombre</TableHeader>
                  <TableHeader>Email</TableHeader>
                  <TableHeader>Teléfono</TableHeader>
                  <TableHeader>Estado</TableHeader>
                  <TableHeader>Acciones</TableHeader>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {conciergeRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <TableCell className="text-gray-500">
                      {formatDate(request.createdAt)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-gray-900">
                        {request.name} {request.lastName}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {request.email}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {request.phone}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={request.status} />
                    </TableCell>
                    <TableCell>
                      <ActionButtons 
                        onApprove={() => handleUpdateStatus(request.id, 'approved', 'concierge')}
                        onReject={() => handleUpdateStatus(request.id, 'rejected', 'concierge')}
                      />
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Planner Requests Table */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <Heading level={3}>Solicitudes de Planners</Heading>
          <div className="mt-4 sm:mt-0 sm:flex-none flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={showDeniedPlanners}
                onChange={(e) => setShowDeniedPlanners(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Mostrar rechazados
            </label>
            <span className="text-sm text-gray-700">
              Total: {planners.filter(p => showDeniedPlanners || !p.denied).length} solicitudes
            </span>
          </div>
        </div>
        {plannersLoading ? (
          <div>Cargando solicitudes...</div>
        ) : (
          <div className="mt-4 overflow-x-auto ring-1 ring-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <TableHeader>Información Personal</TableHeader>
                  <TableHeader>Empresa</TableHeader>
                  <TableHeader>Contacto</TableHeader>
                  <TableHeader>Instagram</TableHeader>
                  <TableHeader>Estado</TableHeader>
                  <TableHeader>Fecha de Solicitud</TableHeader>
                  <TableHeader>Acciones</TableHeader>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {planners.filter(p => showDeniedPlanners || !p.denied).map((planner) => (
                  <tr key={planner.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {planner.firstName ? planner.firstName[0] : '?'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {planner.firstName} {planner.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {planner.uid}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-900">{planner.companyName}</div>
                      {planner.website && (
                        <a
                          href={planner.website.startsWith('http') ? planner.website : `https://${planner.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <GlobeAltIcon className="h-4 w-4" />
                          Website
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-900">{planner.email}</div>
                      <div className="text-sm text-gray-500">{planner.phone}</div>
                    </TableCell>
                    <TableCell>
                      {planner.instagram && (
                        <a
                          href={`https://instagram.com/${planner.instagram.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-pink-600 hover:text-pink-800 flex items-center gap-1"
                        >
                          <LinkIcon className="h-4 w-4" />
                          {planner.instagram}
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={planner.verified ? 'approved' : planner.denied ? 'denied' : 'pending'} />
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-900">
                        {formatDate(planner.createdAt)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        {!planner.verified && !planner.denied && (
                          <>
                            <button
                              onClick={() => handleApprovePlanner(planner)}
                              disabled={isProcessing}
                              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                            >
                              <CheckCircleIcon className="h-4 w-4 mr-1" />
                              Aprobar
                            </button>
                            <button
                              onClick={() => {
                                setSelectedPlanner(planner);
                                setIsModalOpen(true);
                              }}
                              disabled={isProcessing}
                              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                            >
                              <XCircleIcon className="h-4 w-4 mr-1" />
                              Rechazar
                            </button>
                          </>
                        )}
                        {planner.denied && (
                          <div className="text-sm text-gray-500">
                            Razón: {planner.denialReason}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Planner Denial Reason Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => {
          if (!isProcessing) {
            setIsModalOpen(false);
            setSelectedPlanner(null);
            setDenialReason('');
          }
        }}
        title="Razón del Rechazo"
      >
        <DialogBody>
          <DialogTextarea
            label="Razón"
            rows={4}
            placeholder="Ingrese la razón del rechazo..."
            value={denialReason}
            onChange={(e) => setDenialReason(e.target.value)}
            disabled={isProcessing}
          />
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => {
              if (!isProcessing) {
                setIsModalOpen(false);
                setSelectedPlanner(null);
                setDenialReason('');
              }
            }}
            disabled={isProcessing}
          >
            Cancelar
          </Button>
          <Button
            color="red"
            onClick={handleDenyPlanner}
            disabled={!denialReason.trim() || isProcessing}
          >
            {isProcessing ? 'Procesando...' : 'Confirmar Rechazo'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VenueRequests;